import { get, post } from '../../../../service/ApiService';
import { axiosSubscriptionInstance } from '../../../../configs/AxiosInstance';

function mutateFailMessage(error, fallback) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    fallback
  );
}

function isMutateOk(response) {
  if (!response) return false;
  if (response.status != null && response.status !== 200) return false;
  const body = response.data;
  if (body && typeof body === 'object') {
    if (body.success === false || body.status === 'error') return false;
    if (body.detail && (body.success === false || body.error)) {
      return false;
    }
  }
  return true;
}

function extractNotesList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.call_notes)) return response.call_notes;
  if (Array.isArray(response?.data?.call_notes)) return response.data.call_notes;
  if (Array.isArray(response?.notes)) return response.notes;
  if (Array.isArray(response?.data?.notes)) return response.data.notes;
  if (Array.isArray(response?.data) && !response.data.call_notes) {
    return response.data;
  }
  return null;
}

/**
 * Fetch call notes for a given client.
 * @param {String} eventTypeId - Event type ID.
 * @param {String} clientId - Client ID.
 * @returns {Promise<Object>}
 */
export const fetchCallNotes = async (eventTypeId, clientId, options = {}) => {
  const limit = Number.isFinite(options.limit) ? options.limit : 200;
  try {
    const response = await get(
      `/fetch-call-notes?event_type_id=${eventTypeId}&client_id=${clientId}&limit=${limit}`,
      { instance: axiosSubscriptionInstance }
    );

    const notes = extractNotesList(response);
    if (Array.isArray(notes)) {
      return { success: true, data: notes };
    }

    if (response?.data?.total_count === 0 || response?.total_count === 0) {
      return { success: true, data: [] };
    }

    console.warn('Unexpected fetch-call-notes payload shape:', response);
    return {
      success: false,
      message: 'Could not read notes from the server response.',
      data: [],
    };
  } catch (error) {
    console.error('Error fetching notes:', error);
    if (
      error.response?.status === 404 ||
      error.response?.data?.message?.includes('No call notes found')
    ) {
      return { success: true, data: [] };
    }
    return {
      success: false,
      message: mutateFailMessage(
        error,
        'Failed to fetch call notes. Please try again.'
      ),
    };
  }
};

/**
 * Add a new call note.
 * @param {String} eventTypeId - Event type ID.
 * @param {Object} noteData - Note details.
 * @returns {Promise<Object>}
 */
export const addCallNote = async (eventTypeId, noteData) => {
  try {
    const payload = {
      event_type_id: eventTypeId,
      call_notes: [
        {
          operation: 'ADD',
          client_id: noteData.client_id,
          coach_id: noteData.coach_id,
          content: noteData.content,
          duration: noteData.duration || 0,
          call_date: noteData.call_date,
        },
      ],
    };

    const response = await post('/modify-call-notes', payload, {
      instance: axiosSubscriptionInstance,
    });

    if (!isMutateOk(response)) {
      return {
        success: false,
        message:
          response?.data?.detail ||
          response?.data?.message ||
          'Failed to add call note. Please try again.',
      };
    }

    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error adding note:', error);
    return {
      success: false,
      message: mutateFailMessage(
        error,
        'Failed to add call note. Please try again.'
      ),
    };
  }
};

/**
 * Update an existing call note.
 * @param {String} eventTypeId - Event type ID.
 * @param {Object} noteData - Note details with id.
 * @returns {Promise<Object>}
 */
export const updateCallNote = async (eventTypeId, noteData) => {
  try {
    const payload = {
      event_type_id: eventTypeId,
      call_notes: [
        {
          operation: 'UPDATE',
          id: noteData.id,
          client_id: noteData.client_id,
          coach_id: noteData.coach_id,
          content: noteData.content,
          duration: noteData.duration || 0,
          call_date: noteData.call_date,
        },
      ],
    };

    const response = await post('/modify-call-notes', payload, {
      instance: axiosSubscriptionInstance,
    });

    if (!isMutateOk(response)) {
      return {
        success: false,
        message:
          response?.data?.detail ||
          response?.data?.message ||
          'Failed to update call note. Please try again.',
      };
    }

    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error updating note:', error);
    return {
      success: false,
      message: mutateFailMessage(
        error,
        'Failed to update call note. Please try again.'
      ),
    };
  }
};

/**
 * Delete a call note.
 * @param {String} eventTypeId - Event type ID.
 * @param {String} noteId - Note ID to delete.
 * @returns {Promise<Object>}
 */
export const deleteCallNote = async (eventTypeId, noteId) => {
  try {
    const payload = {
      event_type_id: eventTypeId,
      call_notes: [
        {
          operation: 'DELETE',
          id: noteId,
        },
      ],
    };

    const response = await post('/modify-call-notes', payload, {
      instance: axiosSubscriptionInstance,
    });

    if (!isMutateOk(response)) {
      return {
        success: false,
        message:
          response?.data?.detail ||
          response?.data?.message ||
          'Failed to delete call note. Please try again.',
      };
    }

    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting note:', error);
    return {
      success: false,
      message: mutateFailMessage(
        error,
        'Failed to delete call note. Please try again.'
      ),
    };
  }
};
